import moment from 'moment';
import isNil from 'lodash/isNil';
import range from 'lodash/range';
import cloneDeep from 'lodash/cloneDeep';

import {MAX_COHORT_NAME_LENGTH} from 'common/constants';
import {
  formatDate,
  getCalendarDate,
  getCurrentMomentAsOf,
  parseDate,
  parseDatetimeRange,
  parseSpecialDate,
} from 'common/util/date';
import {BarType} from 'common/report/funnels/charts/funnels-step-chart/types';
import {ButtonOption} from 'common/components/popover-card/types';
import {isGroupedEntry, QueryEventEntry, QueryGroupedEntry} from 'common/query-builder/query-entry-section/types';
import {OVERALL_PROP, SEGMENT_SEPARATOR} from 'common/report/funnels/models/constants';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {
  BookmarkFilterByEvent,
  CohortType,
  FunnelBookmark,
  StepSelection,
  TrendUnit,
} from 'common/report/funnels/models/types';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {ArithmeticOperator, Property} from 'common/query-builder/cohorts-query-builder/models/enums/expression-enums';
import {CohortDetail} from 'common/widgets/create-cohort-button';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {
  CalendarDateRange,
  DateRangeType,
  SpecialTimeUnit,
  TimeUnit,
} from 'common/widgets/date-range-picker/models/types';
import {
  isDateSegment,
  isNumericSegment,
  isUserSegment,
  Segment,
} from 'common/query-builder/query-block/segment-menu/types';
import {LogicalOperator, NumberOperator} from 'common/widgets/property-filter-menu/models/types';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {parseNumberRange} from 'common/report/util/sorting/header-sorting';
import {getAlphaLabelFromIndex} from 'common/util';
import {mapEntriesToBookmarkSteps} from 'common/report/funnels/models/format';
import {PropertyType, ResourceType, TimeBucketType} from 'common/report/constants';

import {getGroupLabel} from './index';

export type {ButtonOption};

export function getCohortTitle({
  index,
  type,
  reportName,
  segment,
  dataGroup,
  conversionRange,
  selectedSteps,
}: {
  index: number;
  type: CohortType;
  reportName?: string;
  segment?: number | string;
  conversionRange?: string;
  selectedSteps?: StepSelection;
  dataGroup?: DataGroup;
}) {
  if (!reportName) {
    return ``;
  }

  let stepName;
  if (type === CohortType.CUMULATIVE_DROP_OFF) {
    stepName = `steps 2 - ${index + 1}`;
  } else if (type === CohortType.TIME_BUCKET) {
    stepName = selectedSteps.isEntireRange
      ? `all steps`
      : `step ${selectedSteps.range.from} to step ${selectedSteps.range.to}`;
  } else if (type === CohortType.FREQUENCY_CONVERSION) {
    stepName = `step ${selectedSteps.range.from} ${conversionRange} times before step ${selectedSteps.range.to}`;
  } else if (type === CohortType.FREQUENCY_DROP_OFF) {
    stepName = `step ${selectedSteps.range.to}`;
  } else {
    stepName = `step ${index + 1}`;
  }
  stepName = segment ? `${stepName} (${segment})` : stepName;
  let reportNameTag = `[${reportName}] `;
  let cohortName: string;
  const prefix = dataGroup ? `${getGroupLabel(dataGroup)} that` : `Users who`;

  switch (type) {
    case CohortType.TIME_BUCKET:
      cohortName = `${prefix} completed ${stepName} within ${conversionRange}`;
      break;
    case CohortType.FREQUENCY_CONVERSION:
      cohortName = `${prefix} did ${stepName}`;
      break;
    case CohortType.CONVERTED:
      cohortName = `${prefix} completed ${stepName}`;
      break;
    case CohortType.FREQUENCY_DROP_OFF:
    case CohortType.STEP_DROP_OFF:
      cohortName = `${prefix} did not complete ${stepName}`;
      break;
    case CohortType.CUMULATIVE_DROP_OFF:
      cohortName = `${prefix} didn't complete ${stepName}`;
      break;
    default:
      break;
  }

  const totalChars = reportNameTag.length + cohortName.length;
  if (totalChars > MAX_COHORT_NAME_LENGTH) {
    const truncationIndicator = `...`;
    const charsToTruncate = totalChars - MAX_COHORT_NAME_LENGTH + truncationIndicator.length;
    reportNameTag = `[${reportName.slice(0, reportName.length - charsToTruncate)}${truncationIndicator}] `;
  }

  return `${reportNameTag}${cohortName}`;
}

function getExclusionStepsText(exclusionSteps: QueryEventEntry[]) {
  if (!exclusionSteps.length) {
    return null;
  }

  const exclusionStepsText = exclusionSteps
    .map((step) => {
      const event = step.event.label;
      const exclusionRange =
        step.excludeAll || !(`from` in step.excludeRange)
          ? `all steps`
          : `step ${step.excludeRange.from} and step ${step.excludeRange.to}`;
      return `${event} between ${exclusionRange}`;
    })
    .join(` and `);

  return `Exclusion steps: ${exclusionStepsText}`;
}

/**
 * returns a human readable description of the steps
 */
export function getCohortDescription(cohortType: CohortType, stepNames: string[], dataGroup: DataGroup) {
  const formattedStepNames = stepNames.map((name) => `"${name}"`);
  const stepCount = stepNames.length;
  const prefix = dataGroup ? `${getGroupLabel(dataGroup)} that` : `Users who`;

  switch (cohortType) {
    case CohortType.FREQUENCY_CONVERSION:
    case CohortType.TIME_BUCKET:
    case CohortType.CONVERTED:
      return `${prefix} did ${formattedStepNames.join(` then did `)}`;
    case CohortType.FREQUENCY_DROP_OFF:
      return `${prefix} dropped off after ${formattedStepNames[0]}`;
    case CohortType.CUMULATIVE_DROP_OFF:
      return `${prefix} did ${formattedStepNames[0]} then did NOT do ${formattedStepNames
        .slice(1)
        .join(` AND did NOT do `)}`;
    case CohortType.STEP_DROP_OFF:
      return `${prefix} did ${formattedStepNames.slice(0, stepCount - 1).join(` then did `)} then did NOT do ${
        formattedStepNames[stepCount - 1]
      }`;
    default:
      return ``;
  }
}

function getCohortTimeRange(dateRange) {
  const dateRangeLabel = dateRange.getLabel();
  const capitalizedDateRangeLabel = dateRangeLabel.charAt(0).toUpperCase() + dateRangeLabel.slice(1);
  return `Time Range: ${capitalizedDateRangeLabel}`;
}

function getConversionRangeText(cohortType, conversionRange) {
  if (!conversionRange) {
    return ``;
  }
  switch (cohortType) {
    case CohortType.FREQUENCY_CONVERSION:
      return `Converted after ${conversionRange} times`;
    case CohortType.FREQUENCY_DROP_OFF:
      return `Dropped off after ${conversionRange} times`;
    default:
      return `Converted between: ${conversionRange}`;
  }
}

/** returns a human readable description of the funnel */
export function getCohortText({
  cohortType,
  stepNames,
  exclusionSteps,
  reportName,
  dateRange,
  conversionRange,
  segment,
  dataGroup,
}) {
  const funnelReport = reportName ? `Funnel Report: ${reportName}` : null;
  const description = getCohortDescription(cohortType, stepNames, dataGroup);
  const exclusionStepsText = getExclusionStepsText(exclusionSteps);
  const timeRange = getCohortTimeRange(dateRange);
  const conversionRangeText = getConversionRangeText(cohortType, conversionRange);
  const segmentText = segment || ``;

  // funnelsReport and exclusionSteps can be null
  const textParts = [funnelReport, description, exclusionStepsText, timeRange, conversionRangeText, segmentText].filter(
    Boolean,
  );
  return textParts.join(`.\r\n`);
}

function countingErrorText(selectedBarType, dataGroup, projectAsOfDate) {
  // @ts-ignore
  const now = formatDate(new Date(getCurrentMomentAsOf(projectAsOfDate)));
  const prefix = dataGroup ? `${getGroupLabel(dataGroup, {lowerCase: true})} that` : `users who`;
  if (selectedBarType === BarType.CONVERTED) {
    return `This cohort will not include ${prefix} complete the funnel after ${now}. `;
  } else {
    return `This cohort will include ${prefix} complete the funnel after ${now}. `;
  }
}

/**
 * We want to warn users when they are creating a cohort that might not give users
 * enough time to complete the funnel in the allotted conversion window since this can
 * lead to higher numbers of dropped off users
 * (i.e. when the end of the date range + conversion window is sometime in the future)
 *
 * @return {string|null}
 */
function getCohortWarning(dateRange, conversionWindow, selectedBarType, dataGroup, projectAsOfDate) {
  const dt = parseDate(dateRange.getCalendarDatesAsOf(projectAsOfDate).to);
  const conversionEndDate = moment(dt).add(conversionWindow.value, conversionWindow.unit);
  const now = getCurrentMomentAsOf(projectAsOfDate);

  if (conversionEndDate.isAfter(now)) {
    const conversionEndDateStr = formatDate(conversionEndDate.toDate());
    const suggestedEndDate = now.subtract(conversionWindow.value, conversionWindow.unit);
    const suggestedEndDateStr = formatDate(suggestedEndDate.toDate());

    const groupLabel = getGroupLabel(dataGroup, {lowerCase: true});
    return `${countingErrorText(selectedBarType, dataGroup, projectAsOfDate)}
      Due to the selected conversion window, ${groupLabel} can continue to complete the funnel until ${conversionEndDateStr}.
      To fix this, please move the time window end-date before ${suggestedEndDateStr}.`;
  }
  return null;
}

/**
 * returns metadata about the cohort for the create modal
 */
export function getStepsCohortDetails({
  behaviorDescriptor,
  comparisonDateRange,
  isComparison,
  query,
  reportName,
  projectAsOfDate,
  conversionRange = null,
  segment = null,
  dataGroup = null,
  selectedDateRange = null,
}: {
  isComparison: boolean;
  comparisonDateRange: CalendarDateRange | null;
  behaviorDescriptor: Omit<StepDescriptor, 'type'>;
  query: FunnelsQuery;
  reportName: string;
  projectAsOfDate: string;
  conversionRange?: string;
  segment?: string;
  dataGroup?: DataGroup;
  selectedDateRange?: Partial<DateRange>;
}): CohortDetail[] {
  const {selectedBarType, index} = behaviorDescriptor;
  const stepNames = query.getStepLabels().slice(0, index + 1);
  const {exclusionSteps, conversionWindow} = query;
  let bookmark = query.generateBookmark();
  let dateRange = bookmark.date_range;
  if (selectedDateRange) {
    dateRange = {
      from_date: selectedDateRange.from,
      to_date: selectedDateRange.to,
      type: DateRangeType.Between,
    };
  }
  if (isComparison && comparisonDateRange) {
    const {from, to} = comparisonDateRange;
    const pastPeriodQuery = new FunnelsQuery(query);
    pastPeriodQuery.dateRange = new DateRange({
      type: DateRangeType.Between,
      from,
      to,
    });
    bookmark = pastPeriodQuery.generateBookmark();
  }
  switch (selectedBarType) {
    case BarType.CONVERTED:
      return [
        {
          cohort: createCohort({
            name: getCohortTitle({index, type: CohortType.CONVERTED, reportName, segment, dataGroup}),
            description: getCohortText({
              cohortType: CohortType.CONVERTED,
              stepNames,
              exclusionSteps,
              reportName,
              dateRange: DateRange.fromBookmarkFormat(dateRange),
              conversionRange,
              segment,
              dataGroup,
            }),
            behaviorDescriptor: {
              ...behaviorDescriptor,
              type: CohortType.CONVERTED,
            },
            bookmark,
            query,
            selectedDateRange,
          }),
          label: `Create Cohort`,
          type: CohortType.CONVERTED,
          warning: getCohortWarning(
            DateRange.fromBookmarkFormat(bookmark.date_range),
            conversionWindow,
            BarType.CONVERTED,
            dataGroup,
            projectAsOfDate,
          ),
        },
      ];
    case BarType.DROP_OFF:
      return [
        {
          cohort: createCohort({
            name: getCohortTitle({index, type: CohortType.STEP_DROP_OFF, reportName, segment, dataGroup}),
            description: getCohortText({
              cohortType: CohortType.STEP_DROP_OFF,
              stepNames,
              exclusionSteps,
              reportName,
              dateRange: DateRange.fromBookmarkFormat(bookmark.date_range),
              conversionRange,
              segment,
              dataGroup,
            }),
            behaviorDescriptor: {
              ...behaviorDescriptor,
              type: CohortType.STEP_DROP_OFF,
            },
            bookmark,
            query,
            selectedDateRange,
          }),
          label: index === 1 ? `Create Cohort` : `Drop-offs at this step`,
          type: CohortType.STEP_DROP_OFF,
          warning: getCohortWarning(
            DateRange.fromBookmarkFormat(bookmark.date_range),
            conversionWindow,
            BarType.DROP_OFF,
            dataGroup,
            projectAsOfDate,
          ),
        },
        index > 1 && {
          cohort: createCohort({
            name: getCohortTitle({index, type: CohortType.CUMULATIVE_DROP_OFF, reportName, segment, dataGroup}),
            description: getCohortText({
              cohortType: CohortType.CUMULATIVE_DROP_OFF,
              stepNames,
              exclusionSteps,
              reportName,
              dateRange: DateRange.fromBookmarkFormat(bookmark.date_range),
              conversionRange,
              segment,
              dataGroup,
            }),
            bookmark,
            query,
            behaviorDescriptor: {
              ...behaviorDescriptor,
              type: CohortType.CUMULATIVE_DROP_OFF,
            },
            selectedDateRange,
          }),
          label: `Drop-offs up to this step`,
          type: CohortType.CUMULATIVE_DROP_OFF,
          warning: getCohortWarning(
            DateRange.fromBookmarkFormat(bookmark.date_range),
            conversionWindow,
            BarType.DROP_OFF,
            dataGroup,
            projectAsOfDate,
          ),
        },
      ].filter(Boolean);
    default:
      return [];
  }
}

function getSegments(groupByValue: string, groupBy: Segment[]) {
  const segmentsFromBreakdown = getSegmentsFromBreakdown(groupByValue, groupBy);
  const modifiedSegments = [];
  groupBy.forEach((group, index) => {
    let segmentValue = segmentsFromBreakdown[index];
    // omit segment values that are numeric
    if (isNumericSegment(group) && segmentValue !== `undefined`) {
      return;
    }
    // convert date segment value to backend readable format
    if (isDateSegment(group)) {
      const timeUnit = isUserSegment(group) ? group.user.unit : group.event.unit;
      if (timeUnit === SpecialTimeUnit.HourOfDay || timeUnit === SpecialTimeUnit.DayOfWeek) {
        segmentValue = parseSpecialDate(segmentValue, timeUnit);
      } else {
        const dateRange = parseDatetimeRange(segmentValue, timeUnit, 1);
        const dateTime = Array.isArray(dateRange) ? dateRange[0] : dateRange;
        segmentValue = getCalendarDate(dateTime, true);
      }
    }
    modifiedSegments.push(segmentValue);
  });

  return modifiedSegments;
}

function getSegmentsFromBreakdown(groupByValue: string, groupBy: Segment[]) {
  const segmentValues = !isNil(groupByValue) ? groupByValue.split(SEGMENT_SEPARATOR) : [];
  // omit segment values that are originated from breakdowns
  return segmentValues.slice(segmentValues.length - groupBy.length);
}

function getSegmentsNotFromBreakdown(groupByValue: string, groupBy: Segment[]) {
  const segmentValues = !isNil(groupByValue) ? groupByValue.split(SEGMENT_SEPARATOR) : [];
  // omit segment values that are not originated from breakdowns
  return segmentValues.slice(0, segmentValues.length - groupBy.length);
}

export function getBehaviorParams(behaviorDescriptor: BehaviorDescriptor, groupBy: Segment[]) {
  const {index: uncorrectedIndex, type: cohortType} = behaviorDescriptor;

  const groupByValue = getGroupByValue(behaviorDescriptor);

  // backend is 1-indexed, frontend is 0-indexed
  const index = uncorrectedIndex + 1;

  let behaviorParams: any[] = (function constructBehaviorParameters() {
    if (isStepDescriptor(behaviorDescriptor)) {
      switch (cohortType) {
        case CohortType.CONVERTED:
          return [
            {
              convert_behavior: {step: index},
            },
          ];
        case CohortType.STEP_DROP_OFF:
          return [
            {
              drop_off_behavior: {step: index},
            },
          ];
        case CohortType.CUMULATIVE_DROP_OFF:
          return range(2, index + 1).map((idx) => {
            return {
              drop_off_behavior: {step: idx},
            };
          });
      }
    } else if (isConversionTimeDescriptor(behaviorDescriptor)) {
      let type = TimeBucketType.NORMAL;
      if (behaviorDescriptor.bucket < 0) {
        type = TimeBucketType.LOWER;
      }
      if (behaviorDescriptor.bucket >= behaviorDescriptor.bucketSize) {
        type = TimeBucketType.HIGHER;
      }
      return [
        {
          time_bucket_behavior: {
            step: index,
            bucket_index: behaviorDescriptor.bucket,
            from_start: behaviorDescriptor.fromStart,
            type,
          },
        },
      ];
    } else if (isFrequencyBucketDescriptor(behaviorDescriptor)) {
      return [
        {
          frequency_behavior: {
            step: index,
            bucket_index: behaviorDescriptor.bucket,
            converted: cohortType === CohortType.FREQUENCY_CONVERSION,
          },
        },
      ];
    }

    throw new Error(`unable to construct a behavior for descriptor type ${behaviorDescriptor.type}`);
  })();
  if (!isNil(groupByValue)) {
    behaviorParams = behaviorParams.map((param) => {
      const segments = getSegments(groupByValue, groupBy);
      if (segments.length > 0) param.segments = segments;
      return param;
    });
  }
  return behaviorParams;
}

function convertTrendUnitToTimeUnit(trendUnit: TrendUnit): TimeUnit {
  switch (trendUnit) {
    case TrendUnit.Minute:
      return TimeUnit.Minute;
    case TrendUnit.Hour:
      return TimeUnit.Hour;
    case TrendUnit.Day:
      return TimeUnit.Day;
    case TrendUnit.Week:
      return TimeUnit.Week;
    case TrendUnit.Month:
      return TimeUnit.Month;
    case TrendUnit.Quarter:
      return TimeUnit.Quarter;
    default:
      return null;
  }
}

export function getFilterByEvent(groupByValue: string, groupBy: Segment[]): BookmarkFilterByEvent | null {
  if (isNil(groupByValue)) {
    return null;
  }
  const segments = getSegmentsFromBreakdown(groupByValue, groupBy);
  const filters = [];
  groupBy.forEach((group, index) => {
    if (isNumericSegment(group)) {
      const segmentValue = segments[index];
      const numberRange = parseNumberRange(segmentValue) || [];
      if (!isNil(numberRange[0])) {
        filters.push({
          event: new PropertyFilter({
            ...(isUserSegment(group) ? group.user : group.event),
            filterValue: numberRange[0],
            propertyObjectKey: null,
            filterOperator: NumberOperator.AtLeast,
          }),
        });
      }
      if (!isNil(numberRange[1])) {
        filters.push({
          event: new PropertyFilter({
            ...(isUserSegment(group) ? group.user : group.event),
            filterValue: numberRange[1],
            propertyObjectKey: null,
            filterOperator: NumberOperator.LessThan,
          }),
        });
      }
    }
  });

  if (filters.length > 0) {
    return {
      operator: LogicalOperator.And,
      children: filters,
    };
  }

  return null;
}

function getGroupBy(groupByValue: string, groupBy: Segment[]): Segment[] {
  if (isNil(groupByValue)) {
    return [];
  }
  const segmentValues = getSegmentsFromBreakdown(groupByValue, groupBy);
  return groupBy.filter((group, index) => {
    return !isNumericSegment(group) || segmentValues[index] === `undefined`;
  });
}

function getGroupByValue(behaviorDescriptor: BehaviorDescriptor): string {
  if (
    !isNil(behaviorDescriptor.groupByValue) &&
    behaviorDescriptor.groupByValue !== OVERALL_PROP &&
    typeof behaviorDescriptor.groupByValue === `string`
  ) {
    return behaviorDescriptor.groupByValue;
  } else {
    return undefined;
  }
}

export function resolveCompareEntryToSingleEvent(
  step: QueryGroupedEntry,
  stepNumber: number,
  compareEventsSegment: string,
): QueryEventEntry {
  for (let i = 0; i < step.event.length; i++) {
    const event: QueryEventEntry = step.event[i];
    const eventLabel = event.event.renamed || event.event.label;
    const eventLabelAsSegment = `${stepNumber}${getAlphaLabelFromIndex(i)} - ${eventLabel}`;
    if (compareEventsSegment === eventLabelAsSegment) {
      return event;
    }
  }
  return undefined;
}

export function resolveCompareStepsQueryToConcreteFunnel(
  querySteps: Array<QueryEventEntry | QueryGroupedEntry>,
  compareEventsSegments: string[],
): Array<QueryEventEntry | QueryGroupedEntry> {
  const concreteSteps: Array<QueryEventEntry | QueryGroupedEntry> = [];
  // number of compare steps seen so far.
  let compareStepsCount = 0;
  querySteps.forEach((step, idx) => {
    if (isGroupedEntry(step)) {
      step = resolveCompareEntryToSingleEvent(step, idx + 1, compareEventsSegments[compareStepsCount]);
      compareStepsCount++;
    }
    concreteSteps.push(step);
  });
  return concreteSteps;
}

function formatBookmark(
  bookmark: FunnelBookmark,
  query: FunnelsQuery,
  groupByValue: string,
  groupBy: Segment[],
  selectedDateRange: Partial<DateRange> | null,
): FunnelBookmark {
  const formattedBookmark: FunnelBookmark = bookmark;
  const compareEventsSegments: string[] = getSegmentsNotFromBreakdown(groupByValue, groupBy);
  if (compareEventsSegments.length > 0 || selectedDateRange) {
    const steps: Array<QueryEventEntry | QueryGroupedEntry> = resolveCompareStepsQueryToConcreteFunnel(
      cloneDeep(query.events),
      compareEventsSegments,
    );
    if (selectedDateRange) {
      const {from, to, type} = selectedDateRange;
      const trendUnit = bookmark.trend_unit;
      const filter = new PropertyFilter({
        filterOperator: type,
        filterValue: {
          from,
          to,
          type,
        },
        filterDateUnit: convertTrendUnitToTimeUnit(trendUnit),
        propertyDefaultType: PropertyType.Datetime,
        propertyName: `$time`,
        propertyObjectKey: null,
        propertyType: PropertyType.Datetime,
        resourceType: ResourceType.Event,
      });
      steps[0].filters = [...steps[0].filters, filter];
      // If there is a selected date range, we can set a new time window (date range) of the funnel bookmark
      formattedBookmark.date_range = {
        from_date: getCalendarDate(selectedDateRange.from),
        to_date: getCalendarDate(selectedDateRange.to),
        type: selectedDateRange.type,
      };
    }
    formattedBookmark.steps = mapEntriesToBookmarkSteps(steps);
  }
  return formattedBookmark;
}

export function createCohort({
  behaviorDescriptor,
  name,
  description,
  bookmark,
  query,
  selectedDateRange = null,
}: {
  behaviorDescriptor: BehaviorDescriptor;
  name: string;
  description: string;
  bookmark: FunnelBookmark;
  query: FunnelsQuery;
  selectedDateRange?: Partial<DateRange>;
}): Cohort {
  const groupBy: Segment[] = bookmark.group_by || [];
  const groupByValue: string | symbol = getGroupByValue(behaviorDescriptor);

  const behaviorParams = getBehaviorParams(behaviorDescriptor, groupBy);
  const filterByEvent = getFilterByEvent(groupByValue, groupBy);
  const filteredGroupBy = getGroupBy(groupByValue, groupBy);
  const formattedBookmark = formatBookmark(bookmark, query, groupByValue, groupBy, selectedDateRange);

  const behaviors = {
    A0: {
      funnel_report: {
        bookmark: {
          ...formattedBookmark,
          group_by: filteredGroupBy,
          ...(filterByEvent && {filter_by_event: filterByEvent}),
        },
        behavior_params: behaviorParams,
      },
    },
  };

  const selector = {
    operator: ArithmeticOperator.Equals,
    children: [
      {
        property: Property.Behaviors,
        value: `A0`,
      },
      {
        property: Property.Literal,
        value: true,
      },
    ],
  };

  return Cohort.parseCohort({
    behaviors,
    name,
    description,
    selector,
    ...(formattedBookmark.data_group_id ? {data_group_id: formattedBookmark.data_group_id} : {}),
  });
}

/** represents a step index and the type of bar clicked */
export interface StepDescriptor {
  index: number;
  type: CohortType;
  selectedBarType: BarType;
  groupByValue: string; // segmented value
}

export function isStepDescriptor(descriptor: BehaviorDescriptor): descriptor is StepDescriptor {
  return [CohortType.STEP_DROP_OFF, CohortType.CUMULATIVE_DROP_OFF, CohortType.CONVERTED].includes(descriptor.type);
}

/** represents a cohort based on a conversion-time bucket */
export interface ConversionTimeDescriptor {
  bucket: number;
  bucketSize: number;
  index: number;
  fromStart: boolean;
  groupByValue: string | symbol;
  type: CohortType.TIME_BUCKET;
}

export function isConversionTimeDescriptor(descriptor: BehaviorDescriptor): descriptor is ConversionTimeDescriptor {
  return descriptor.type === CohortType.TIME_BUCKET;
}

/** represents a cohort for frequency chart */
export interface FrequencyBucketDescriptor {
  bucket: number;
  index: number;
  fromStart: boolean;
  groupByValue: string;
  type: CohortType.FREQUENCY_CONVERSION | CohortType.FREQUENCY_DROP_OFF;
}

export function isFrequencyBucketDescriptor(descriptor: BehaviorDescriptor): descriptor is FrequencyBucketDescriptor {
  return descriptor.type === CohortType.FREQUENCY_CONVERSION || descriptor.type === CohortType.FREQUENCY_DROP_OFF;
}

/**
 * a specific item from the UI used to describe a cohort
 *
 * ex: a single bar segment on the chart, a single bucket in the time-to-convert
 */
export type BehaviorDescriptor = StepDescriptor | ConversionTimeDescriptor | FrequencyBucketDescriptor;
